// useEmblaPeekWidth.ts
import type { EmblaCarouselType } from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

/**
 * A Tailwind-friendly helper shape for allowed slide widths.
 * `cls` must be a Tailwind class (e.g., "w-52").
 * `px` is the corresponding pixel width (e.g., 208).
 */
type WidthOpt = { cls: string; px: number };

/**
 * useEmblaPeekWidth
 * -----------------
 * Picks a Tailwind width class for Embla slides so the viewport always shows:
 *   - exactly one (excepition small tablets, can be more) fully-visible card,
 *   - a guaranteed "peek" of the next card,
 *
 * How it works
 * - Measures the *actual visible content width* (viewport width minus current
 *   right padding and container paddings).
 * - Chooses the largest width option that still:
 *     (a) leaves at least `minPeek` of the next slide visible, and
 *     (b) does NOT allow two full slides to fit.
 * - If an edge case still fits two fully, it steps down one size.
 * - Finally, it grows the viewport's `padding-right` so the second slide remains
 *   partial by at least `minPeek`.
 *
 * Requirements
 * - Tailwind must include all width classes you may return (via safelist).
 * - Your track should use real gap/margins between slides (e.g., `space-x-2`);
 *   this hook measures the actual gap between the first two slides.
 *
 * @param emblaApi Embla API instance from `useEmblaCarousel`
 * @param params.widths Ordered list of width choices (ascending by px)
 * @param params.minPeek Minimum pixels of the next card to keep visible
 * @returns `wClass` — the chosen Tailwind width class (e.g., "w-52")
 *
 * @example
 * const WIDTHS = [{cls:"w-52",px:208},{cls:"w-56",px:224},{cls:"w-60",px:240}]
 * const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" })
 * const wClass = useEmblaPeekWidth(emblaApi, { widths: WIDTHS, minPeek: 75 })
 * // ...
 * <div ref={emblaRef} className="overflow-hidden embla__viewport">
 *   <div className="flex px-4 space-x-2 embla__container">
 *     <div className={clsx("embla__slide flex-none pb-1", wClass)}>…</div>
 *   </div>
 * </div>
 */
export const useEmblaPeekWidth = (
  emblaApi: EmblaCarouselType | undefined,
  { widths, minPeek = 40 }: { widths: WidthOpt[]; minPeek?: number },
) => {
  const [wClass, setWClass] = useState(widths[0]?.cls ?? "");

  // Largest that shows a peek but doesn't allow two full
  const pickByConstraints = useCallback(
    (content: number, gap: number) => {
      for (let i = widths.length - 1; i >= 0; i--) {
        const w = widths[i].px;
        const peekOk = content >= w + gap + minPeek;
        const twoFit = content >= w * 2 + gap;
        if (peekOk && !twoFit) {
          return widths[i];
        }
      }
      // fallback: largest that at least shows a peek
      for (let i = widths.length - 1; i >= 0; i--) {
        const w = widths[i].px;
        if (content >= w + gap + minPeek) {
          return widths[i];
        }
      }
      return widths[0];
    },
    [widths, minPeek],
  );

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const viewport = emblaApi.rootNode();
    const container = emblaApi.containerNode();

    const recalc = () => {
      const vw = viewport.getBoundingClientRect().width;

      const cs = getComputedStyle(container);
      const padL = parseFloat(cs.paddingLeft || "0");
      const padR = parseFloat(cs.paddingRight || "0");

      // include current pr in the measurement (we modify it below)
      const vcs = getComputedStyle(viewport);
      const prNow = parseFloat(vcs.paddingRight || "0");

      const content = vw - padL - padR - prNow;

      const slides = emblaApi.slideNodes();
      const gap =
        slides.length >= 2
          ? slides[1].getBoundingClientRect().left -
            slides[0].getBoundingClientRect().right
          : 8;

      // pick a width
      let chosen = pickByConstraints(content, gap);

      // guard: if two still fit, step down one size
      if (content >= chosen.px * 2 + gap - 0.5) {
        const idx = widths.findIndex((w) => w.cls === chosen.cls);
        if (idx > 0) {
          chosen = widths[idx - 1];
        }
      }

      setWClass(chosen.cls);

      // enforce min peek
      const contentNoPr = vw - padL - padR;
      const prNeeded = contentNoPr - (chosen.px * 2 + gap) + 1; // +1 to beat rounding
      const pr = Math.max(minPeek, prNeeded, 0);
      viewport.style.paddingRight = `${pr}px`;
    };

    recalc();
    emblaApi.on("reInit", recalc);
    window.addEventListener("resize", recalc);
    return () => {
      emblaApi.off("reInit", recalc);
      window.removeEventListener("resize", recalc);
    };
  }, [emblaApi, widths, minPeek, pickByConstraints]);

  return wClass;
};
